/**
 * `GET /api/app/min-version` — C1 force-upgrade gate response.
 *
 * Why this contract is the "master lever" (per API_RESPONSE_AUDIT §1):
 * every other deferred contract migration relies on the mobile app being
 * forcibly upgradable below a known floor. A rename of `minVersion` or a
 * shift of `updateUrl.ios` to top level would either fail-open (no version
 * is ever "below") or false-block the entire installed base. Pinning the
 * response shape here is what keeps the rest of the contract surface
 * negotiable later.
 *
 * Discriminator note: the route returns two non-overlapping shapes —
 *   - 200 success: `{ minVersion, latestVersion, updateUrl: { ios, android } }`
 *   - 503 error:   `{ code: "MIN_VERSION_UNAVAILABLE" }`
 *
 * These are not a classical discriminated union (different field sets, no
 * common discriminator field), but the HTTP status is the discriminator
 * the client uses to branch. We export both shapes plus a tagged-union
 * convenience type clients can build after they read the status — keeps
 * the wire-level body schema honest while letting the mobile gate
 * reason about success-vs-error in one switch.
 *
 * @see app/api/app/min-version/route.ts — handler
 * @see apps/mobile/lib/version-gate.ts — VersionGateConfig consumer
 * @see .tondocs/payments/API_CONTRACT_HARDENING.md C1
 * @see .tondocs/payments/API_RESPONSE_AUDIT.md §1
 */

import { z } from "zod";
import { pinned } from "./pin";

export interface MinVersionResponse {
  minVersion: string;
  latestVersion: string;
  updateUrl: {
    ios: string;
    android: string;
  };
}

export interface MinVersionUnavailableResponse {
  code: "MIN_VERSION_UNAVAILABLE";
}

export const MinVersionResponseSchema = pinned<MinVersionResponse>()(
  z.object({
    minVersion: z.string().min(1),
    latestVersion: z.string().min(1),
    updateUrl: z.object({
      ios: z.string(),
      android: z.string(),
    }),
  }),
);

export const MinVersionUnavailableResponseSchema =
  pinned<MinVersionUnavailableResponse>()(
    z.object({
      code: z.literal("MIN_VERSION_UNAVAILABLE"),
    }),
  );
